import React, { useEffect, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import moment from 'moment';
import { Alert, Button, Descriptions, Modal, Select, Space, Table } from 'antd';
import { DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
// Api
import { GET_TICKET_EVENT_ORDERS } from 'api/ticketing/queries';
// Types
import {
  GetTicketEventOrders,
  GetTicketEventOrdersVariables,
} from 'api/ticketing/types/GetTicketEventOrders';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import {
  SortDirection,
  TicketEventOrderSort,
  TicketEventStatus,
  TicketOrderKind,
  TicketOrderStatus,
} from 'api/graphql-global-types';
// Hooks
import { useServerTable } from 'pages/Events/hooks/useServerTable';
import { useOrderActions } from './hooks/useOrderActions';
// Helpers
import {
  canRefundOrder,
  canRevokeComp,
  formatEventDateTime,
  formatMoney,
  getCardLabel,
  getTicketingErrorMessage,
  getTicketsLabel,
  ORDER_SORT_COLUMNS,
  ORDER_STATUS_FILTER_OPTIONS,
  OrderRow,
  pluralize,
} from 'pages/Events/helpers';
import { downloadCsv } from 'helpers/csv';
import { toOrdersCsvRows } from './helpers';
// Constants
import { isoDateFormat } from 'constants/global';
import {
  BULK_REFUND_WAIT_MS,
  EVENTS_PAGE_SIZE,
  REFUND_POLL_INTERVAL_MS,
} from 'pages/Events/constants';
// UI
import PageToolbar from 'ui/PageToolbar/PageToolbar';
import { errorNotification } from 'ui/Notification';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import OrderDetails from 'pages/Events/components/OrderDetails/OrderDetails';
import {
  OrderCustomer,
  OrderPayment,
  OrderReference,
  OrderStatus,
  OrderTickets,
} from 'pages/Events/components/OrderCells/OrderCells';
import BulkRefundModal from '../BulkRefundModal/BulkRefundModal';
// Styles
import styles from './OrdersTable.module.scss';

type EventDetails = TicketEventDetailsFields;

const EXPORT_PAGE_SIZE = 100;

type OrdersTableProps = {
  event: EventDetails;
  kind: TicketOrderKind;
  toolbarActions?: React.ReactNode;
};

const OrdersTable = ({
  event,
  kind,
  toolbarActions,
}: OrdersTableProps): JSX.Element => {
  const client = useApolloClient();
  const isComp = kind === TicketOrderKind.Comp;
  const [statuses, setStatuses] = useState<TicketOrderStatus[]>([]);
  const table = useServerTable({
    sortColumns: ORDER_SORT_COLUMNS,
    defaultSortBy: TicketEventOrderSort.CreatedAt,
    defaultPageSize: EVENTS_PAGE_SIZE,
  });
  const [exporting, setExporting] = useState(false);
  const [bulkRefundOpen, setBulkRefundOpen] = useState(false);
  const [bulkRefundQueued, setBulkRefundQueued] = useState(false);

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<
    GetTicketEventOrders,
    GetTicketEventOrdersVariables
  >(GET_TICKET_EVENT_ORDERS, {
    variables: {
      input: {
        ticketEventId: event.id,
        kinds: [kind],
        statuses: statuses.length ? statuses : null,
        sortBy: table.sortBy,
        direction: table.direction,
        offset: table.offset,
        limit: table.pageSize,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { refundOrder, revokeComp } = useOrderActions();

  const orders = data?.ticketEventOrders.entities ?? [];
  const total = data?.ticketEventOrders.total ?? 0;
  const refundInFlight = orders.some(
    (row) => row.status === TicketOrderStatus.RefundPending
  );

  // Stripe settles a refund in the background; keep asking until it lands.
  // A refund-all is picked up by a worker first, so poll until it shows up.
  useEffect(() => {
    if (refundInFlight || bulkRefundQueued) {
      startPolling(REFUND_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [refundInFlight, bulkRefundQueued, startPolling, stopPolling]);

  useEffect(() => {
    if (refundInFlight) {
      setBulkRefundQueued(false);
    }
  }, [refundInFlight]);

  useEffect(() => {
    if (!bulkRefundQueued) {
      return undefined;
    }

    const timeout = setTimeout(
      () => setBulkRefundQueued(false),
      BULK_REFUND_WAIT_MS
    );

    return () => clearTimeout(timeout);
  }, [bulkRefundQueued]);

  const confirmRefund = (row: OrderRow): void => {
    Modal.confirm({
      title: `Refund order ${row.reference}?`,
      icon: <ExclamationCircleOutlined />,
      width: 520,
      content: (
        <>
          <Descriptions column={1} size="small" className={styles.confirmList}>
            <Descriptions.Item label="Customer">
              {row.customerName || '—'} {row.email ? `(${row.email})` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Tickets">
              {getTicketsLabel(row)}
            </Descriptions.Item>
            <Descriptions.Item label="Refund">
              {formatMoney(row.amount)}{' '}
              {getCardLabel(row) && `to ${getCardLabel(row)}`}
            </Descriptions.Item>
          </Descriptions>
          <p>
            The full amount goes back through Stripe. The tickets stay valid
            until Stripe confirms, then they&apos;re revoked. This can&apos;t be
            undone.
          </p>
        </>
      ),
      okText: `Refund ${formatMoney(row.amount)}`,
      okButtonProps: { danger: true },
      onOk: async () => {
        await refundOrder(row);
        refetch();
      },
    });
  };

  const confirmRevoke = (row: OrderRow): void => {
    Modal.confirm({
      title: `Revoke comp ${row.reference}?`,
      icon: <ExclamationCircleOutlined />,
      content: `${getTicketsLabel(row)} for ${
        row.customerName || 'this guest'
      } stop working at the door and go back on sale. Tickets already scanned in can't be revoked.`,
      okText: 'Revoke',
      okButtonProps: { danger: true },
      onOk: async () => {
        await revokeComp(row);
        refetch();
      },
    });
  };

  const handleExport = async (): Promise<void> => {
    setExporting(true);

    try {
      const rows: OrderRow[] = [];
      let offset = 0;
      let count = Infinity;

      while (offset < count) {
        // eslint-disable-next-line no-await-in-loop
        const { data: pageData } = await client.query<
          GetTicketEventOrders,
          GetTicketEventOrdersVariables
        >({
          query: GET_TICKET_EVENT_ORDERS,
          variables: {
            input: {
              ticketEventId: event.id,
              kinds: [kind],
              statuses: statuses.length ? statuses : null,
              sortBy: TicketEventOrderSort.CreatedAt,
              direction: SortDirection.ASC,
              offset,
              limit: EXPORT_PAGE_SIZE,
            },
          },
          fetchPolicy: 'network-only',
        });
        rows.push(...(pageData?.ticketEventOrders.entities || []));
        count = pageData?.ticketEventOrders.total ?? 0;
        offset += EXPORT_PAGE_SIZE;
      }

      downloadCsv(
        `${event.slug}-${isComp ? 'comps' : 'orders'}-${moment().format(
          isoDateFormat
        )}.csv`,
        toOrdersCsvRows(rows, event.timezone)
      );
    } catch (exportError) {
      errorNotification(
        getTicketingErrorMessage(exportError, 'Failed to export the orders')
      );
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: isComp ? 'Comp' : 'Order',
      key: 'reference',
      sorter: true,
      sortOrder: table.sortOrder('reference'),
      render: (_: unknown, row: OrderRow) => <OrderReference order={row} />,
    },
    {
      title: isComp ? 'Guest' : 'Customer',
      key: 'customer',
      sorter: true,
      sortOrder: table.sortOrder('customer'),
      render: (_: unknown, row: OrderRow) => <OrderCustomer order={row} />,
    },
    {
      title: 'Tickets',
      key: 'tickets',
      sorter: true,
      sortOrder: table.sortOrder('tickets'),
      render: (_: unknown, row: OrderRow) => <OrderTickets order={row} />,
    },
    ...(isComp
      ? [
          {
            title: 'Note',
            key: 'note',
            render: (_: unknown, row: OrderRow) => row.note || '—',
          },
        ]
      : [
          {
            title: 'Paid',
            key: 'amount',
            sorter: true,
            sortOrder: table.sortOrder('amount'),
            render: (_: unknown, row: OrderRow) => (
              <OrderPayment order={row} showPromoCode />
            ),
          },
        ]),
    {
      title: 'Date',
      key: 'createdAt',
      sorter: true,
      sortOrder: table.sortOrder('createdAt'),
      render: (_: unknown, row: OrderRow) =>
        formatEventDateTime(row.createdAt, event.timezone),
    },
    {
      title: 'Status',
      key: 'status',
      sorter: true,
      sortOrder: table.sortOrder('status'),
      render: (_: unknown, row: OrderRow) => (
        <OrderStatus order={row} timezone={event.timezone} />
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_: unknown, row: OrderRow) =>
        canRefundOrder(row.status, row.kind) ? (
          <Button size="small" danger onClick={() => confirmRefund(row)}>
            {row.refundFailure ? 'Retry refund' : 'Refund'}
          </Button>
        ) : canRevokeComp(row.status, row.kind) ? (
          <Button size="small" danger onClick={() => confirmRevoke(row)}>
            Revoke
          </Button>
        ) : null,
    },
  ];

  return (
    <div className={styles.root}>
      <PageToolbar
        leading={
          <Select<TicketOrderStatus[]>
            mode="multiple"
            allowClear
            aria-label="Filter by status"
            placeholder={
              isComp ? 'All comps' : 'All orders except abandoned checkouts'
            }
            value={statuses}
            options={ORDER_STATUS_FILTER_OPTIONS}
            onChange={(value) => {
              setStatuses(value);
              table.setPage(1);
            }}
            className={styles.statusFilter}
          />
        }
      >
        <Space wrap>
          {toolbarActions}
          {!isComp && event.status === TicketEventStatus.Cancelled && (
            <Button danger onClick={() => setBulkRefundOpen(true)}>
              Refund all paid orders
            </Button>
          )}
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={handleExport}
            disabled={!total}
          >
            Export CSV
          </Button>
        </Space>
      </PageToolbar>

      {(refundInFlight || bulkRefundQueued) && (
        <Alert
          className={styles.alert}
          type="info"
          showIcon
          message="Refunds are on their way through Stripe. This list updates by itself."
        />
      )}

      {error ? (
        <LoadErrorAlert
          message="Failed to load the orders"
          error={error}
          onRetry={refetch}
        />
      ) : (
        <Table<OrderRow>
          rowKey="orderId"
          columns={columns}
          dataSource={orders}
          loading={loading && !data}
          onChange={table.onChange}
          scroll={{ x: 960 }}
          locale={{
            emptyText: isComp ? 'No comps issued yet.' : 'No orders yet.',
          }}
          expandable={{
            expandedRowRender: (row) => (
              <OrderDetails order={row} timezone={event.timezone} />
            ),
          }}
          pagination={{
            current: table.page,
            pageSize: table.pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => pluralize(count, isComp ? 'comp' : 'order'),
          }}
        />
      )}

      {!isComp && (
        <BulkRefundModal
          event={event}
          visible={bulkRefundOpen}
          onClose={(started) => {
            setBulkRefundOpen(false);
            setBulkRefundQueued(Boolean(started));
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default OrdersTable;
